"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MapPin, Search, TrendingUp, Target, ChartBar as BarChart3, Users, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Compass } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const marketMetrics = [{
  id: 1,
  label: "Local Commercial Intent",
  value: "82%",
  description: "of Gloucester GL postcode search queries demonstrate immediate intent to hire or buy.",
  highlight: "High Conversion Potential"
}, {
  id: 2,
  label: "Google Map Pack Clicks",
  value: "64%",
  description: "of local search users click exclusively within the top 3 Google Map Pack results.",
  highlight: "Map Pack Dominance"
}, {
  id: 3,
  label: "Conversational & Voice Queries",
  value: "+45%",
  description: "year-on-year increase in GL1-GL4 local voice search and localized AI prompts.",
  highlight: "AI & Voice Expansion"
}, {
  id: 4,
  label: "Unclaimed Search Demand",
  value: "71%",
  description: "of Gloucester SME competitors lack targeted local landing pages and technical schema.",
  highlight: "Competitor Vulnerability"
}];
const customerBehaviors = [{
  id: 1,
  title: "Postcode-Specific Search Intent",
  description: "Gloucester customers rarely search with generic national terms. They actively combine specialized services with hyper-local modifiers such as GL1 city center, GL2 Quedgeley, GL3 Churchdown, and GL4 Barnwood.",
  badge: "Hyper-Local Targeting"
}, {
  id: 2,
  title: "Trust Verification via Map Reviews",
  description: "Before visiting a premises or requesting a quote, 89% of Gloucester consumers examine Google Business Profile reviews, local citations, and localized case studies to verify expertise.",
  badge: "Local Authority"
}, {
  id: 3,
  title: "Mobile-First Micro-Moments",
  description: "Over 68% of Gloucester local searches occur on mobile devices during active decision-making windows, demanding fast page speeds, precise click-to-call options, and clear local directional cues.",
  badge: "Mobile Optimization"
}];
const strategyFramework = [{
  id: 1,
  stepNumber: "01",
  title: "Google Business Profile & Map Pack Supremacy",
  description: "I optimize your Google Business Profile with complete category structuring, hyper-local geotagged media, citation cleanup across UK directories, and active local review acquisition frameworks.",
  focus: "Local Visibility"
}, {
  id: 2,
  stepNumber: "02",
  title: "Localized Content & Postcode Landing Pages",
  description: "I construct dedicated, high-intent landing pages tailored specifically to Gloucester neighborhoods and surrounding Gloucestershire hubs without content duplication or keyword stuffing.",
  focus: "Relevance & Authority"
}, {
  id: 3,
  stepNumber: "03",
  title: "Local Schema Markup & Technical Alignment",
  description: "I embed precise LocalBusiness JSON-LD schema, geotargeted metadata, microdata coordinates, and core web vitals optimization to ensure Google explicitly understands your local footprint.",
  focus: "Technical Accuracy"
}, {
  id: 4,
  stepNumber: "04",
  title: "Gloucestershire Backlink & Citation Building",
  description: "I acquire genuine, high-authority editorial backlinks and citations from respected Gloucestershire business directories, regional news outlets, and local industry organizations.",
  focus: "Trust Signals"
}];
export default function GloucesterSearch() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".gsap-reveal", {
        y: 30
      });
      gsap.to(".gsap-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".gsap-reveal", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="gloucesterlocalsearchanalysis" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 gsap-reveal" style={{
        opacity: 0
      }}>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary border border-accent/30 text-accent-foreground text-xs font-semibold mb-4">
            <MapPin className="size-3.5 text-accent" />
            <span>Gloucestershire Search Insights</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-primary tracking-tight mb-6">
            Gloucester Local Search Analysis &amp; Market Opportunity
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            When prospective clients in Gloucester search for specialized services, heritage trade, manufacturing expertise, or professional consultancy across GL postcodes, 78% of purchasing decisions start on Google. Here is my breakdown of local search behavior and how you can capture dominant market share.
          </p>
        </div>

        {/* Highlight Feature with Visual */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-20 gsap-reveal" style={{
        opacity: 0
      }}>
          <div className="lg:col-span-7 relative rounded-2xl overflow-hidden border border-border shadow-md bg-card">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/gloucester_local_seo_landscape.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/gloucester_local_seo_landscape.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/gloucester_local_seo_landscape.png-wvc-sizes" alt="Gloucester local search market analysis and strategy breakdown" className="w-full h-80 sm:h-96 object-cover" loading="lazy" decoding="async" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-transparent to-transparent flex items-end p-6 sm:p-8">
              <div className="text-background">
                <Badge variant="secondary" className="mb-2 bg-accent text-accent-foreground border-none font-medium">
                  Direct Expert Analysis
                </Badge>
                <p className="text-sm sm:text-base font-medium opacity-95">
                  "You work directly with me to transform raw Gloucester search demand into predictable local leads and direct customer calls."
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-center space-y-6">
            <div className="p-6 rounded-xl bg-card border border-border shadow-xs">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                  <Search className="size-5" />
                </div>
                <h3 className="text-lg font-serif font-semibold text-primary">
                  The Gloucester Search Landscape
                </h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                The Gloucester economy is undergoing rapid digital growth across GL1 through GL4. Consumers are increasingly searching with high specificity—bypassing generic directories in favor of direct, authoritative local specialists who rank cleanly in local organic and Map Pack positions.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-secondary/50 border border-accent/20 shadow-xs">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2.5 rounded-lg bg-accent/20 text-accent-foreground">
                  <TrendingUp className="size-5" />
                </div>
                <h3 className="text-lg font-serif font-semibold text-primary">
                  Unlocking First-Page Dominance
                </h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                My tailored Gloucester SEO methodology replaces generic nationwide tactics with precise geographical targeting, robust local trust signals, and clean technical execution that search algorithms prioritize.
              </p>
            </div>
          </div>
        </div>

        {/* Data Cards Grid */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-10 gsap-reveal" style={{
          opacity: 0
        }}>
            <h3 className="text-2xl font-serif font-bold text-primary mb-3">
              Key Gloucester Local Search Indicators
            </h3>
            <p className="text-muted-foreground text-sm">
              Verified data signals highlighting consumer search patterns across the Gloucester region.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {marketMetrics.map((metric, i) => <Card key={metric.id} data-index={i} className="gsap-reveal border-border/80 shadow-xs hover:shadow-md transition-shadow duration-200 bg-card" style={{
            opacity: 0
          }}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="border-accent/40 text-accent font-medium text-[13px] px-2 py-0.5">
                      {metric.highlight}
                    </Badge>
                  </div>
                  <div className="text-3xl sm:text-4xl font-serif font-bold text-primary tracking-tight">
                    {metric.value}
                  </div>
                  <CardTitle className="text-base font-semibold text-foreground mt-1">
                    {metric.label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs text-muted-foreground leading-normal">
                    {metric.description}
                  </CardDescription>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* Consumer Behavior Breakdown */}
        <div className="bg-card rounded-2xl p-8 sm:p-12 border border-border shadow-sm mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5 gsap-reveal" style={{
            opacity: 0
          }}>
              <div className="inline-flex items-center gap-2 text-accent font-semibold text-xs tracking-wider uppercase mb-3">
                <Compass className="size-4 text-accent" />
                <span>Consumer Intent</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-serif font-bold text-primary mb-4">
                Understanding How Gloucester Customers Search Online
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                Local search behavior in Gloucester differs significantly from broader national trends. Customers demand high contextual relevance, nearby physical proof, and instant clarity before initiating contact.
              </p>
              
              <div className="space-y-3 pt-2 border-t border-border">
                <div className="flex items-start gap-2.5 text-xs text-foreground/90">
                  <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                  <span>Geographically refined searches (GL1 city center to GL4 business parks)</span>
                </div>
                <div className="flex items-start gap-2.5 text-xs text-foreground/90">
                  <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                  <span>High sensitivity to local review quantity and recency</span>
                </div>
                <div className="flex items-start gap-2.5 text-xs text-foreground/90">
                  <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                  <span>Preference for direct local consultants over nationwide agencies</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-4">
              {customerBehaviors.map((item, i) => <div key={item.id} data-index={i} className="gsap-reveal p-6 rounded-xl bg-background border border-border hover:border-accent/40 transition-colors duration-200" style={{
              opacity: 0
            }}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <h4 className="font-serif font-semibold text-base text-primary">
                      {item.title}
                    </h4>
                    <span className="text-[13px] font-medium px-2.5 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                      {item.badge}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* 4-Step Strategy Framework */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12 gsap-reveal" style={{
          opacity: 0
        }}>
            <Badge variant="secondary" className="mb-3 bg-secondary text-secondary-foreground border border-accent/20">
              My Execution Roadmap
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-serif font-bold text-primary mb-4">
              How I Position Your Business at the Top of Gloucester Search
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              A structured, highly targeted local SEO methodology designed specifically for sustained growth across Gloucester and Gloucestershire markets.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {strategyFramework.map((step, i) => <div key={step.id} data-index={i} className="gsap-reveal p-6 sm:p-8 rounded-xl bg-card border border-border/80 shadow-xs hover:border-primary/40 transition-all duration-200 flex flex-col justify-between" style={{
            opacity: 0
          }}>
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-serif font-bold text-accent">
                      {step.stepNumber}
                    </span>
                    <Badge variant="outline" className="text-[12px] text-muted-foreground border-border">
                      {step.focus}
                    </Badge>
                  </div>
                  <h4 className="text-lg font-serif font-semibold text-primary mb-3">
                    {step.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                    {step.description}
                  </p>
                </div>
                <div className="pt-4 border-t border-border/60 flex items-center text-xs font-medium text-primary">
                  <span>Direct Consultant Implementation</span>
                  <ShieldCheck className="size-3.5 ml-2 text-accent" />
                </div>
              </div>)}
          </div>
        </div>

        {/* Final CTA Strip */}
        <div className="gsap-reveal bg-primary text-primary-foreground rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden shadow-xl" style={{
        opacity: 0
      }}>
          <div className="relative z-10 max-w-3xl mx-auto">
            <h3 className="text-xl sm:text-2xl font-serif font-bold mb-4 text-primary-foreground">
              Ready to Capture Dominant Search Share in Gloucester?
            </h3>
            <p className="text-primary-foreground/90 text-sm sm:text-base leading-relaxed mb-8 max-w-2xl mx-auto">
              You will work directly with me—Yassine Essaadi—to build a bespoke local SEO strategy that drives real business impact, qualified local inquiries, and long-term organic growth.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-medium">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 dark:hover:bg-primary-foreground/20">
                <Link to="/LocalSEOPage">
                  <span>Explore My Local SEO Services</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}